// 261023 E3-P3h4c (FE) — vỏ Modal «hộp hậu quả» dùng chung cho 4 thao tác. Logic ở `useConsequencePreview` / `consequenceView` (có jest); thân ở `ConsequenceBody`.
// Luật: preview lỗi / BE cũ ⇒ HỘP CŨ (`fallback`), vẫn bấm được; `canCommit:false` ⇒ nút khoá; 409 (dữ liệu đổi) ⇒ báo câu không mã + tải lại hệ quả, giữ hộp mở;
// một nút nhấn mạnh duy nhất (nút xác nhận, `danger` khi thao tác phá).
import React, { useEffect, useState } from 'react';
import { Modal, message } from 'antd';
import type { ConsequencePreview } from '../../../types/adaptationWorkbench';
import ConsequenceBody from './ConsequenceBody';
import { consequenceErrorView, ConsequenceView } from './consequenceView';
import { useConsequencePreview } from './useConsequencePreview';

interface Props {
  open: boolean;
  title: string;
  lead: string;
  okText: string;
  danger?: boolean;
  /** GET hệ quả (chỉ đọc). */
  load: () => Promise<ConsequencePreview>;
  /** Hộp xác nhận CŨ — vẽ khi preview không dùng được. */
  fallback: React.ReactNode;
  /** Câu dự phòng khi mutation lỗi mà BE không có chữ đọc được. */
  errorFallback: string;
  /** Gọi mutation. `view` = hệ quả đang xem (mang `base` để gửi lại mốc); `null` = đang dùng hộp cũ. Lỗi phải được ném ra (không nuốt). */
  onConfirm: (view: ConsequenceView | null) => Promise<void>;
  /** Lỗi KHÔNG phải «dữ liệu đổi»: caller tự báo (vd hộp lỗi riêng của khung xương); mặc định toast chữ đã lọc. Hộp đóng sau đó. */
  onError?: (e: unknown) => void | Promise<void>;
  onClose: () => void;
}

const ConsequenceModal: React.FC<Props> = ({ open, title, lead, okText, danger, load, fallback, errorFallback, onConfirm, onError, onClose }) => {
  const cons = useConsequencePreview(open, load);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => { if (!open) { setNotice(null); setBusy(false); } }, [open]);

  const blocked = cons.status === 'loading' || (cons.status === 'ready' && !!cons.view && !cons.view.canCommit);
  const submit = async () => {
    if (busy || blocked) return;
    setBusy(true);
    try {
      await onConfirm(cons.status === 'ready' ? cons.view : null);
      onClose();
    } catch (e) {
      const v = consequenceErrorView(e, errorFallback);
      if (v.stale) { setNotice(v.text); cons.reload(); } else { onClose(); if (onError) await onError(e); else message.error(v.text, 8); }
    } finally { setBusy(false); }
  };

  return (
    <Modal
      open={open} title={title} okText={okText} cancelText="Thôi" maskClosable={false} confirmLoading={busy}
      onCancel={() => { if (!busy) onClose(); }} onOk={() => { void submit(); }}
      okButtonProps={{ danger: !!danger, disabled: blocked || busy }} cancelButtonProps={{ disabled: busy }} destroyOnClose
    >
      <ConsequenceBody lead={lead} status={cons.status} view={cons.view} notice={notice} fallback={fallback} />
    </Modal>
  );
};

export default ConsequenceModal;
